import { type AutoCompleteItem } from '@atlassian/eoc-auto-complete';
import { type IFilterValues } from '@atlassian/eoc-condition-filter';
import { type WeekDay } from '@atlassian/eoc-day-time-interval';
// this response is coming from webapi/user/notificationsInfo?isDeleted=false&userId=5bc5bdc9-f560-4865-b705-5f8ede05af9e address
// if we want to change availableActionTypes, we should make a new contract with backend.
export enum AVAILABLE_RULE_TYPES {
	'New Alert' = 0,
	'Schedule Start' = 3,
	'Schedule End' = 7,
	'Incoming Call Routing' = 8,
	'Acknowledged Alert' = 1,
	'Closed Alert' = 2,
	'Assigned Alert' = 5,
	'Add Note' = 6,
}

export enum OperationIds {
	Matches = 0,
	Contains = 1,
	StartsWith = 3,
	EndsWith = 4,
	Equals = 5,
	ContainsKey = 6,
	ContainsValue = 7,
	GreaterThan = 8,
	LessThan = 9,
	IsEmpty = 10,
	EqualsIgnoreWhitespace = 11,
}

export enum ReverseOperations {
	'matches' = 0,
	'contains' = 1,
	'starts-with' = 3,
	'ends-with' = 4,
	'equals' = 5,
	'contains-key' = 6,
	'contains-value' = 7,
	'greater-than' = 8,
	'less-than' = 9,
	'is-empty' = 10,
	'equals-ignore-whitespace' = 11,
}

export enum MatchConditionType {
	'match-all' = 0,
	'match-any-condition' = 1,
	'match-all-conditions' = 2,
}

export enum Operation {
	Matches = 'matches',
	Contains = 'contains',
	StartsWith = 'starts-with',
	EndsWith = 'ends-with',
	Equals = 'equals',
	ContainsKey = 'contains-key',
	ContainsValue = 'contains-value',
	GreaterThan = 'greater-than',
	LessThan = 'less-than',
	IsEmpty = 'is-empty',
	EqualsIgnoreWhitespace = 'equals-ignore-whitespace',
}

export interface Condition {
	customKey?: string;
	expectedValue: string;
	field: string;
	fieldFreeForm?: boolean;
	not: boolean;
	operation: OperationIds;
	order?: number;
}

export interface EditDescriptionEndpoint {
	nextPreferenceId: string | null;
	preferenceId: string;
}

export interface Rule {
	conditionMatchType: number;
	conditions: Condition[];
	default: boolean;
	enabled: boolean;
	loopAfter: number;
	name: string;
	notificationRuleType?: string;
	order: number;
	preferenceId: string;
	rules: RuleMeta[];
	scheduleIds?: string[];
	scheduleNotifyTimes?: number[];
	timeRestrictedInterval: TimeRestrictedInterval;
	userId: string;
}

export interface TimeRestriction {
	endDay?: WeekDay;
	endHour: number;
	endMinute: number;
	startDay?: WeekDay;
	startHour: number;
	startMinute: number;
}

export interface TimeRestrictedInterval {
	appliedTimeZone: string;
	enabled: boolean;
	timeRestrictions: TimeRestriction[];
}

// TODO: this is shared among multiple package, might unify
export type ContactMethodTypes = 'email' | 'sms' | 'mobile' | 'voice';

export interface RuleMeta {
	contactId?: string;
	enabled: boolean;
	mobileSound?: string;
	roleBasedContactDefinition?: {
		method: ContactMethodTypes;
		targetOrder: number;
	};
	ruleId?: string;
	ruleTime: number;
}

export interface Contact {
	addressDisplay: string;
	contactId: string;
	disabledReason: string;
	enabled: boolean;
	method: 'EMAIL' | 'SMS' | 'MOBILE' | 'VOICE';
	verified: boolean;
}

export type RuleType = keyof typeof AVAILABLE_RULE_TYPES;

interface NotificationPreferencesResponseBase {
	requestId: string;
	took: number;
}

// I initially thought of using the discriminated union for the rules as well,
// but because of the many layers of array mapping, it proved to be too cumbersome
// and not worth the effort.
export interface NotificationPreferencesResponse
	extends NotificationPreferencesResponseBase,
		Record<RuleType, Rule[]> {
	roleBasedNotificationName?: undefined;
}

export interface RoleBasedNotificationResponse
	extends NotificationPreferencesResponseBase,
		Record<RuleType, Rule[]> {
	roleBasedNotificationName: string;
	roleBasedNotificationRole: string;
}

export type ContactType = 'Email' | 'SMS' | 'Voice' | 'Mobile App';

export type Contacts = Record<ContactType, Contact[]>;

export interface ContactResponse {
	contacts: Contacts;
}

export interface CreateNotificationPreference {
	conditionMatchType: number;
	conditions: Condition[];
	enabled: boolean;
	loopAfter: number;
	name: string;
	notificationRuleType: RuleType;
	rules: RuleMeta[];
	scheduleIds?: { id: string }[];
	scheduleNotifyTimes?: number[];
	timeRestrictedInterval: TimeRestrictedInterval;
}

export interface UpdateNotificationPreference extends CreateNotificationPreference {
	preferenceId: string;
}

export interface UpdatePartialNotificationPreference {
	enabled: boolean;
}

export interface UpdatePartialNotificationRule {
	payload: Partial<Contact>;
	preferenceGroup: RuleType;
	preferenceId: string;
	ruleId: string;
}

export interface DeleteNotificationRule {
	preferenceGroup: RuleType;
	preferenceId: string;
	ruleId: string;
}

export interface CreateNotificationRule {
	onCompleted?: () => void;
	payload: {
		contactId: string;
		enabled: boolean;
		mobileSound: string;
		ruleTime: number;
	};
	preferenceGroup: RuleType;
	preferenceId: string;
}

export interface UpdateNotificationRule {
	onCompleted?: () => void;
	payload: {
		contactId: string;
		enabled: boolean;
		mobileSound: string;
		ruleId: string;
		ruleTime: number;
	};
	preferenceGroup: RuleType;
	preferenceId: string;
	ruleId: string;
}

export interface ContactMethodOptions extends Contact {
	label: string;
	value: string;
}

export interface MuteResponse {
	muted: boolean;
	muteUntil: number;
}

interface TimeRestrictions {
	endDay: number | null;
	endHour: number | null;
	endMinute: number | null;
	startDay: number | null;
	startHour: number | null;
	startMinute: number | null;
}

export interface QuietHoursResponse {
	quietHours: {
		appliedTimeZone: string;
		enabled: boolean;
		timeRestrictions: Array<TimeRestrictions>;
	};
}

export interface ForwardingRule {
	endDate: number;
	fromUserId: string;
	id: string;
	startDate: number;
	toUserId: string;
}

export interface ForwardingRulesResponse {
	values: ForwardingRule[];
}

export type ForwardingRulePayload = Pick<ForwardingRule, 'toUserId' | 'startDate' | 'endDate'>;

export interface ForwardingRuleFormState {
	endDate: string;
	startDate: string;
	toUser: AutoCompleteItem;
}

export interface ForwardingRuleHandlers {
	handleAddClick: () => void;
}

export interface FormData {
	customTimeRestrictions: TimeRestriction[];
	dailyTimeRestrictionsFrom: string;
	dailyTimeRestrictionsTo: string;
	filterAlerts: Pick<IFilterValues, 'conditions'> & {
		matchType: MatchConditionType;
	};
	loopAfter: string;
	name: string;
	notificationMethods: {
		editingMethodId?: string;
		isAddingMethod?: boolean;
		rules: RuleMeta[];
	};
	repeat: boolean;
	ruleType: { label: string; value: string };
	schedule: { label: string; value: string }[];
	timeRestrictedIntervalEnabled: boolean;
	timeSlotRadio: string;
	timings: string[];
}

export type ProductType = 'jsm' | 'compass';
